import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, timeAgo } from "@/lib/api";
import { ArticleEditor, EpisodePicker, RulesTab, SettingsTab } from "@/components/articles/ArticleAdminParts";
import { ScorePill } from "@/components/articles/ScorePanel";

const TABS = [["drafts", "Drafts"], ["published", "Published"], ["rules", "Rules"], ["settings", "Settings"], ["log", "Activity"]];
const OUTCOME = { written: "text-emerald-300", demand_check: "text-violet-300", indexed: "text-teal-300", no_topic: "text-slate-300", transcribed: "text-sky-300", skipped: "text-slate-400", failed: "text-rose-300", rejected: "text-amber-300" };
const LABEL = { written: "Article written", demand_check: "Search demand", indexed: "Pushed to search", no_topic: "No evergreen topic", transcribed: "Transcribed", skipped: "Skipped", failed: "Failed (will retry)", rejected: "Rejected" };

const ArticleList = ({ status, onOpen }) => {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/admin/articles/list", { params: { status } }).then((r) => setItems(r.data)); }, [status]);
  if (!items) return <Loader2 className="animate-spin text-slate-400" />;
  if (!items.length) return <p className="text-sm text-slate-400 py-10 text-center" data-testid={`${status}-empty`}>{status === "draft" ? "No drafts waiting. New episodes are checked every 10 minutes." : "Nothing published yet."}</p>;
  return (
    <ul className="space-y-2" data-testid={`${status}-list`}>
      {items.map((a) => (
        <li key={a.id}><button onClick={() => onOpen(a.id)} className="w-full text-left rounded-2xl card-surface p-4 flex gap-4 items-center hover:border-sky-400/40 transition-colors" data-testid="article-row">
          {(a.illustration_url || a.image_url) && <img src={a.illustration_url || a.image_url} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0" />}
          <div className="min-w-0 flex-1">
            <p className="eyebrow text-[0.65rem]">{a.content_type_name} · {a.show_name}</p>
            <p className="font-display font-bold leading-snug truncate" data-testid="article-row-headline">{a.headline}</p>
            <p className="text-xs text-slate-400 truncate">{a.standfirst}</p>
          </div>
          <div className="text-right text-xs text-slate-400 shrink-0 space-y-1">
            <div className="flex justify-end gap-1"><ScorePill label="AI" value={a.quality_score} testId="row-quality-score" /><ScorePill label="SEO" value={a.seo_score} testId="row-seo-score" /></div>
            <p>{a.word_count.toLocaleString()} words{status === "published" && <span data-testid="article-row-views"> · {a.views} views</span>} · {timeAgo(a.published_at || a.created_at)}</p>
          </div>
        </button></li>
      ))}
    </ul>
  );
};

const LogTab = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/articles/log").then((r) => setRows(r.data)); }, []);
  if (!rows) return <Loader2 className="animate-spin text-slate-400" />;
  if (!rows.length) return <p className="text-sm text-slate-400 py-10 text-center" data-testid="log-empty">Nothing processed yet.</p>;
  return (
    <ul className="divide-y divide-white/5 rounded-2xl card-surface" data-testid="log-list">
      {rows.map((r) => (
        <li key={r._id} className="px-4 py-3 flex flex-wrap gap-x-4 gap-y-1 items-baseline" data-testid="log-row" data-outcome={r.outcome}>
          <span className={`text-xs font-mono uppercase tracking-widest w-40 shrink-0 ${OUTCOME[r.outcome] || ""}`}>{LABEL[r.outcome] || r.outcome}</span>
          <span className="text-sm font-medium flex-1 min-w-[12rem]">{r.episode_title} <span className="text-slate-400 font-normal">· {r.show_name}</span></span>
          <span className="text-xs text-slate-400 w-full sm:w-auto sm:max-w-md">{r.reason}</span>
          <span className="text-xs text-slate-500 ml-auto">{timeAgo(r.created_at)}</span>
        </li>
      ))}
    </ul>
  );
};

export default function ArticlesAdmin() {
  const [tab, setTab] = useState("drafts");
  const [counts, setCounts] = useState({});
  const [types, setTypes] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [key, setKey] = useState(0);
  const refresh = useCallback(() => { api.get("/admin/articles/counts").then((r) => setCounts(r.data)); window.dispatchEvent(new Event("articles-changed")); setKey((k) => k + 1); }, []);
  useEffect(() => { refresh(); api.get("/admin/articles/rules").then((r) => setTypes(r.data.content_types)); }, [refresh]);
  return (
    <div className="space-y-6" data-testid="articles-admin">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="eyebrow mb-1">Underdog Stories</p>
          <h1 className="font-display text-3xl font-black tracking-tight">SEO Articles</h1>
          <p className="text-sm text-slate-400 mt-1" data-testid="articles-counts">{counts.drafts ?? 0} drafts · {counts.published ?? 0} published · {counts.queued ?? 0} episodes queued · {counts.written_today ?? 0} written today</p>
        </div>
        <EpisodePicker onDone={refresh} />
      </div>
      {openId ? <ArticleEditor id={openId} contentTypes={types} onClose={() => { setOpenId(null); refresh(); }} onChanged={refresh} /> : (
        <>
          <nav className="flex gap-1 border-b border-white/10" data-testid="articles-tabs">
            {TABS.map(([k, label]) => <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === k ? "border-sky-400 text-white" : "border-transparent text-slate-400 hover:text-white"}`} data-testid={`articles-tab-${k}`}>{label}{k === "drafts" && counts.drafts ? <span className="ml-2 rounded-full bg-sky-400 text-[#070F1E] px-1.5 text-[0.65rem]" data-testid="drafts-badge">{counts.drafts}</span> : null}</button>)}
          </nav>
          <div key={key}>
            {tab === "drafts" && <ArticleList status="draft" onOpen={setOpenId} />}
            {tab === "published" && <ArticleList status="published" onOpen={setOpenId} />}
            {tab === "rules" && <RulesTab />}
            {tab === "settings" && <SettingsTab />}
            {tab === "log" && <LogTab />}
          </div>
        </>
      )}
    </div>
  );
}
